import { useSelector } from "react-redux";
import { useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { CrisisSupportInfo } from "@/components/Consent/CrisisSupportInfo";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getPhotoIDFileKey } from "@/core/helpers/photoID";
import { Heading2 } from "@/ui2/atoms/Heading";
import { PhotoIDUpload } from "@/features/photo-id/PhotoIDUpload";
import type { PhotoIDUploadStatus } from "@/features/photo-id/types";
import { selectPatientId } from "@/redux/selectors/authSelectors";
import { showToast, type ToastParams } from "@/providers/ToastProvider";
import { useLogInTaskContext } from "@/containers/Dashboard/Patient/LogInTaskContext";
import { useUpdateMemberProfile } from "@/features/patient/api/mutations/useUpdateMemberProfile";

export function UploadPhotoIDLoginTask() {
  const patientId = useSelector(selectPatientId);
  const [uploadStatus, setUploadStatus] = useState<PhotoIDUploadStatus>("idle");
  const { isError, mutate: updateProfile, status } = useUpdateMemberProfile();
  const { onTaskCompleted, onTaskSkipped } = useLogInTaskContext();

  const isUploadComplete = uploadStatus === "success";

  const handleConfirm = () => {
    updateProfile(
      {
        personal_id_front: getPhotoIDFileKey({ id: patientId, side: "front" }),
        username: patientId,
      },
      {
        onError: () => {
          showToast(errorToast);
        },
        onSuccess: onTaskCompleted,
      },
    );
  };

  return (
    <Card2 className="tw:mx-auto tw:flex-col tw:gap-8 tw:p-12 tw:lg:max-w-140">
      <Heading2 as="h1" className="tw:text-center" size={4}>
        ID Verification
      </Heading2>

      <p className="body-md" data-testid="explanationTextLabel">
        We need a photo of your government-issued ID to verify your identity. This is legally
        required for all telehealth sessions. It will be securely stored and only accessible to your
        care team.
      </p>

      <PhotoIDUpload onParentStatusChange={setUploadStatus} patientId={patientId} />

      {isUploadComplete && (
        <Button2
          aria-label="Confirm ID upload and proceed"
          data-testid="confirmBtn"
          isLoading={status === "loading"}
          onPress={handleConfirm}
          {...getAutomationDataAttr("photo-id-confirm-button")}
        >
          {isError ? "Retry" : "Confirm"}
        </Button2>
      )}

      {(!isUploadComplete || isError) && <SkipUploadSection onSkip={onTaskSkipped} />}

      <Divider2 />
      <CrisisSupportInfo />
    </Card2>
  );
}

function SkipUploadSection({ onSkip }: { onSkip: () => void }) {
  return (
    <p className="body-md tw:self-start">
      Don't have your ID handy?
      <br />
      <Button2
        aria-label="Skip photo ID upload"
        className="tw:font-normal"
        data-testid="skipBtn"
        onPress={onSkip}
        size="md"
        variant="link"
        {...getAutomationDataAttr("photo-id-skip-button")}
      >
        Skip for now
      </Button2>{" "}
      – you can still join your session and add your ID upon next login.
    </p>
  );
}

const errorToast: ToastParams = [
  {
    heading: "Profile update error",
    text: (
      <>
        We couldn't update your patient profile.
        <br />
        Please try again or contact support if the problem persists.
      </>
    ),
    variant: "error",
  },
];
